import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IDataImportService } from "services/DataImportService";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { clearActiveSubmission, selectActiveSubmission } from "../../ActiveSubmissionSlice";
import { createSubmission, getActiveSubmission } from "../../../shared/submissionThunks";
import { findSheetMappingsForWorksheet, toSheetMapping } from "../../sheetMappings";
import { clearUpload } from "../FileUpload/FileUploadSlice";
import { selectParsedColumnSources } from "../Parsing/ParsingSlice";
import { clearWorksheetMapping } from "./mappingThunks";
import { ResolvedMapping, ResolvedMappingState } from "./mappingResolution";
import {
  buildSheetColumnMappings,
  HydratedMapping,
  isMappingUnchanged,
  MappingWithRowIdentifiers,
  resolveMappingRows,
} from "./columnMappingRows";

type MappingState = {
  // Effective column mapping committed by the Mapping step's "Confirm all and continue".
  // Consumed by the Validation step; rebuilt on every pass through Mapping, or hydrated from
  // GetColumnMappings on a resumed submission.
  ResolvedMapping: ResolvedMappingState | null;
  ResolvedRowIdentifierFields: Record<string, string[]>;
  SavedRowIdentifierFields: Record<string, string[]>;
  // Last mapping known to be on the server (successful SaveColumnMappings or GetColumnMappings hydration).
  // Lets an unchanged re-confirm skip the per-sheet-mapping SaveColumnMappings round-trips. Deliberately NOT
  // ResolvedMapping itself: a failed save leaves that committed locally but unsaved.
  SavedMapping: ResolvedMapping | null;
};

const initialState: MappingState = {
  ResolvedMapping: null,
  ResolvedRowIdentifierFields: {},
  SavedRowIdentifierFields: {},
  SavedMapping: null,
};

// Sheet mappings give SaveColumnMappings/GetColumnMappings their submissionSheetMappingID. Usually
// already in the upload slice (saved or hydrated); fetched directly on a cold resume that skipped Upload.
async function loadSheetMappings(state: RootState, dataImportService: IDataImportService, submissionID: string) {
  const stateSheetMappings = state.dataImportUpload.SheetMappings;
  if (stateSheetMappings && stateSheetMappings.length > 0) return stateSheetMappings;
  return (await dataImportService.getSheetMappings(submissionID)).map(toSheetMapping);
}

// Persists the committed mapping — one BulkSaveColumnMappings call carrying every sheet mapping's column
// mappings, each stamped columnMappingsConfirmed.
export const saveColumnMappings = createAsyncThunk(
  "DataImport/SaveColumnMappings",
  async (
    arg: { submissionID: string; resolved: ResolvedMappingState; rowIdentifierFields: Record<string, string[]> },
    { getState }
  ): Promise<MappingWithRowIdentifiers> => {
    const state = getState() as RootState;
    const submission = selectActiveSubmission(state);
    if (!submission || submission.submissionID !== arg.submissionID) throw new Error("No active submission");

    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    const sheetMappings = await loadSheetMappings(state, dataImportService, arg.submissionID);
    const parsedColumnSources = selectParsedColumnSources(state);

    // Worksheets without a saved sheet mapping are skipped, not an error: the Mapping step renders every
    // template worksheet on a resumed session, but only associated sheets are part of the
    // submission — anything else is excluded downstream anyway.
    const savable = Object.entries(arg.resolved.mapping)
      .map(([worksheet, fields]) => ({
        worksheet,
        fields,
        sheetMappingsForWorksheet: findSheetMappingsForWorksheet(sheetMappings, worksheet),
      }))
      .filter((entry) => entry.sheetMappingsForWorksheet.length > 0);

    const savableMapping: ResolvedMapping = Object.fromEntries(savable.map((entry) => [entry.worksheet, entry.fields]));
    const mappingToSave: MappingWithRowIdentifiers = {
      mapping: savableMapping,
      rowIdentifierFields: Object.fromEntries(
        Object.entries(arg.rowIdentifierFields).filter(([worksheet]) => worksheet in savableMapping)
      ),
    };

    // Re-confirming an unchanged mapping skips the BulkSaveColumnMappings round-trip.
    const { SavedMapping, SavedRowIdentifierFields } = state.dataImportMapping;
    if (isMappingUnchanged(mappingToSave, SavedMapping, SavedRowIdentifierFields)) return mappingToSave;

    const sheetColumnMappings = savable.flatMap(({ worksheet, fields, sheetMappingsForWorksheet }) =>
      buildSheetColumnMappings(
        fields,
        sheetMappingsForWorksheet,
        parsedColumnSources?.[worksheet] ?? [],
        mappingToSave.rowIdentifierFields[worksheet] ?? []
      )
    );

    await dataImportService.saveColumnMappings(arg.submissionID, sheetColumnMappings);

    return mappingToSave;
  }
);

// Rehydrates the committed mapping on resume so Validation can run without revisiting Mapping.
export const hydrateColumnMappings = createAsyncThunk(
  "DataImport/GetColumnMappings",
  async (arg: { submissionID: string }, { getState }): Promise<HydratedMapping | null> => {
    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    const rows = await dataImportService.getColumnMappings(arg.submissionID);
    if (rows.length === 0) return null;

    const sheetMappings = await loadSheetMappings(getState() as RootState, dataImportService, arg.submissionID);
    return resolveMappingRows(rows, sheetMappings);
  }
);

export const mappingSlice = createSlice({
  name: "dataImportMapping",
  initialState,
  reducers: {
    setResolvedMapping: (state, action: PayloadAction<ResolvedMappingState>) => {
      state.ResolvedMapping = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(hydrateColumnMappings.fulfilled, (state, action) => {
        // The hydrated copy IS the server state — remember it for the unchanged-save check.
        state.SavedMapping = action.payload?.resolved.mapping ?? null;
        state.SavedRowIdentifierFields = action.payload?.rowIdentifierFields ?? {};
        if (action.payload && Object.keys(state.ResolvedRowIdentifierFields).length === 0) {
          state.ResolvedRowIdentifierFields = action.payload.rowIdentifierFields;
        }
        // A committed-in-session mapping outranks the persisted copy — don't clobber it.
        if (!state.ResolvedMapping && action.payload) {
          state.ResolvedMapping = action.payload.resolved;
        }
      })
      .addCase(saveColumnMappings.fulfilled, (state, action) => {
        state.ResolvedRowIdentifierFields = action.payload.rowIdentifierFields;
        state.SavedMapping = action.payload.mapping;
        state.SavedRowIdentifierFields = action.payload.rowIdentifierFields;
      })
      .addCase(clearWorksheetMapping.fulfilled, (state, action) => {
        if (!action.payload) return;
        for (const worksheet of action.meta.arg.worksheets) {
          if (state.ResolvedMapping) {
            delete state.ResolvedMapping.mapping[worksheet];
            delete state.ResolvedMapping.resolutions[worksheet];
          }
          if (state.SavedMapping) delete state.SavedMapping[worksheet];
          delete state.ResolvedRowIdentifierFields[worksheet];
          delete state.SavedRowIdentifierFields[worksheet];
        }
      })
      // The mapping describes the uploaded file(s): reset when the active submission changes
      // or the upload is discarded.
      .addCase(getActiveSubmission.pending, () => initialState)
      .addCase(createSubmission.fulfilled, () => initialState)
      .addCase(clearActiveSubmission, () => initialState)
      .addCase(clearUpload, () => initialState);
  },
});

export const { setResolvedMapping } = mappingSlice.actions;

export const selectResolvedMapping = (state: RootState) => state.dataImportMapping.ResolvedMapping;
export const selectSavedMapping = (state: RootState) => state.dataImportMapping.SavedMapping;
export const selectResolvedRowIdentifierFields = (state: RootState) =>
  state.dataImportMapping.ResolvedRowIdentifierFields;

export default mappingSlice.reducer;
